import { useEffect, useMemo, useState } from 'react';
import { transactionService } from '../services/api-secure';
import CalmToast from './shared/CalmToast';
import { useAuth } from '../contexts/AuthContext-secure';
import { Spinner } from './Spinner';
import { calmTokens as t } from '../shared/tokens';
import {
  deriveUserDisplay,
  fmtEUR,
  greetingPrefix,
  groupByDate,
  toIsoDay,
} from '../shared/format';
import { buildTransactionPayload, computeStatsMeta } from './transactions/helpers';
import InitialBalanceCard from './transactions/InitialBalanceCard';
import { IconPlus, IconSparkle } from '../shared/icons';
import CalmStatCard from './transactions/CalmStatCard';
import CalmDayGroup from './transactions/CalmDayGroup';
import CalmMonthPicker from './transactions/CalmMonthPicker';
import CalmTransactionModal from './transactions/CalmTransactionModal';
import CalmDeleteModal from './transactions/CalmDeleteModal';

const emptyNewTx = () => ({
    date: toIsoDay(new Date()),
    description: '',
    type: 'debit',
    amount: '',
    withdrawnFromPersonal: true,
    withdrawnFromBank: false,
    category: 'uncategorized',
});

export default function TransactionsView({ month, year, onMonthChange }) {
    const { user } = useAuth();
    const [monthData, setMonthData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [newTransaction, setNewTransaction] = useState(emptyNewTx);
    const [deletingTransaction, setDeletingTransaction] = useState(null);
    const [feedback, setFeedback] = useState({ message: '', type: '', visible: false, actions: null });

    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

    useEffect(() => {
        if (user?.id) loadMonth();
    }, [month, year, user?.id]);

    const loadMonth = async () => {
        setLoading(true);
        try {
            const data = await transactionService.getMonth(monthKey);
            setMonthData(data);
        } catch (error) {
            console.error('Error loading month:', error);
        }
        setLoading(false);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const transaction = {
                id: crypto.randomUUID(),
                date: new Date(newTransaction.date),
                description: newTransaction.description,
                amount: parseFloat(newTransaction.amount),
                type: newTransaction.type,
                withdrawnFromPersonal: newTransaction.withdrawnFromPersonal,
                withdrawnFromBank: newTransaction.withdrawnFromBank,
                category: newTransaction.category,
            };
            await transactionService.addTransaction(monthKey, transaction);
            await loadMonth();
            setNewTransaction(emptyNewTx());
            setAddOpen(false);
        } catch (error) {
            console.error('Error adding transaction:', error);
        }
        setLoading(false);
    };

    const handleInitialBalanceSave = async (value) => {
        setLoading(true);
        try {
            await transactionService.updateInitialBalance(monthKey, value);
            await loadMonth();
        } catch (error) {
            console.error('Error updating initial balance:', error);
        }
        setLoading(false);
    };

    const toggleWithdrawn = async (transactionId, field) => {
        setLoading(true);
        try {
            const original = monthData.transactions.find((t) => t._id === transactionId);
            if (!original) {
                console.error('Transaction non trouvée avec ID:', transactionId);
                setLoading(false);
                return;
            }
            const cleanTransaction = buildTransactionPayload(original, {
                [field]: !original[field],
            });
            await transactionService.updateTransaction(original._id, cleanTransaction);
            await loadMonth();
        } catch (error) {
            console.error('Error toggling withdrawn status:', error);
            if (error.data?.errors) {
                console.error('Détails des erreurs de validation:', error.data.errors);
            }
        }
        setLoading(false);
    };

    const handleDelete = (transactionId) => {
        const tx = monthData?.transactions.find((t) => t._id === transactionId);
        if (!tx) return;
        setDeletingTransaction(tx);
    };

    const confirmDelete = async () => {
        if (!deletingTransaction) return;
        setLoading(true);
        try {
            await transactionService.deleteTransaction(deletingTransaction._id);
            setDeletingTransaction(null);
            await loadMonth();
        } catch (error) {
            console.error('Error deleting transaction:', error);
        }
        setLoading(false);
    };

    const handleEdit = (transaction) => {
        setEditingTransaction({
            ...transaction,
            date: toIsoDay(transaction.date),
        });
    };

    const closeFeedback = () => setFeedback((f) => ({ ...f, visible: false }));

    const showFeedback = (message, type = 'info', { actions = null, autoHide = true } = {}) => {
        setFeedback({ message, type, visible: true, actions });
        if (autoHide && type !== 'error') {
            setTimeout(() => setFeedback((f) => (f.message === message ? { ...f, visible: false } : f)), 4000);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const cleanTransaction = buildTransactionPayload(editingTransaction);
            await transactionService.updateTransaction(editingTransaction._id, cleanTransaction);
            setEditingTransaction(null);
            await loadMonth();
        } catch (error) {
            console.error('Error updating transaction:', error);
            if (error.data?.errors) {
                console.error('Détails des erreurs de validation (édition):', error.data.errors);
            }
            showFeedback('La modification a échoué.', 'error');
        }
        setLoading(false);
    };

    const sortedTransactions = useMemo(() => {
        if (!monthData) return [];
        return [...monthData.transactions].sort((a, b) => {
            const da = a.date instanceof Date ? a.date : new Date(a.date);
            const db = b.date instanceof Date ? b.date : new Date(b.date);
            return db - da;
        });
    }, [monthData]);

    const grouped = useMemo(() => groupByDate(sortedTransactions), [sortedTransactions]);
    const statsMeta = useMemo(() => computeStatsMeta(sortedTransactions), [sortedTransactions]);

    if (!monthData) {
        return (
            <div
                style={{
                    display: 'grid',
                    placeItems: 'center',
                    padding: 40,
                    color: t.textDim,
                }}
            >
                <Spinner className="w-8 h-8" />
            </div>
        );
    }

    const { firstName } = deriveUserDisplay(user?.email);
    const stats = monthData.stats;
    const txCount = sortedTransactions.length;
    const personalPositive = stats.personalBalance >= 0;

    const heroHint = personalPositive
        ? 'Vous avez de la marge pour ce mois-ci'
        : 'Le mois est un peu serré — surveillez les sorties';

    const heroAccent = 'ce mois-ci';
    const heroTitle = personalPositive ? 'Tout est sous contrôle' : 'Prudence';

    return (
        <div style={{ color: t.text, fontFamily: t.fontUi }}>
            {/* Le sélecteur de mois est aligné sur le centre du bloc de texte,
                pas sur son bas : c'est ce qui le fait paraître décroché. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--hero-mb)',
                    gap: 20,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ minWidth: 0 }}>
                    <div
                        className="calm-hero-greeting"
                        style={{
                            fontSize: 13,
                            color: t.textDim,
                            marginBottom: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <IconSparkle size={13} style={{ color: t.accent }} />{' '}
                        {greetingPrefix()} {firstName}
                    </div>
                    {/* En paysage, le compteur remonte sur la ligne du titre
                        (voir `.calm-hero-line` dans index.css). */}
                    <div className="calm-hero-line">
                        <h1
                            style={{
                                fontFamily: t.fontDisp,
                                fontSize: 'var(--hero-size)',
                                fontWeight: 400,
                                margin: 0,
                                letterSpacing: -1,
                                lineHeight: 1.1,
                                color: t.text,
                            }}
                        >
                            {heroTitle}{' '}
                            <span style={{ color: personalPositive ? t.credit : t.debit, fontStyle: 'italic' }}>
                                {heroAccent}
                            </span>
                            .
                        </h1>
                        <div
                            className="calm-hero-count"
                            style={{
                                fontSize: 14,
                                color: t.textDim,
                                marginTop: 8,
                            }}
                        >
                            {txCount === 0
                                ? 'Aucune opération pour le moment'
                                : `${txCount} opération${txCount > 1 ? 's' : ''} enregistrée${txCount > 1 ? 's' : ''}`}
                        </div>
                    </div>
                </div>

                <CalmMonthPicker month={month} year={year} onChange={onMonthChange} />
            </div>

            {/* En paysage, cette enveloppe réunit les deux grilles en une
                seule rangée de tuiles (voir `.calm-stats-wrap`). */}
            <div className="calm-stats-wrap">
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 'var(--stats-gap)',
                        marginBottom: 'var(--stats-row1-mb)',
                    }}
                    className="calm-stats-row-1"
                >
                    <CalmStatCard
                        label="Solde personnel"
                        value={fmtEUR(stats.personalBalance, { sign: stats.personalBalance < 0 })}
                        tone={personalPositive ? 'credit' : 'debit'}
                        hint={heroHint}
                        big
                        featured
                    />
                    <CalmStatCard
                        label="Total crédit"
                        value={fmtEUR(stats.totalCredit, { sign: stats.totalCredit > 0 })}
                        tone="credit"
                        meta={
                            statsMeta.credit.count > 0
                                ? [
                                      {
                                          value: String(statsMeta.credit.count),
                                          label: statsMeta.credit.count > 1 ? 'opérations' : 'opération',
                                      },
                                      ...(statsMeta.credit.lastDate
                                          ? [{ value: statsMeta.credit.lastDate, label: 'dernière' }]
                                          : []),
                                  ]
                                : undefined
                        }
                        hint={statsMeta.credit.count === 0 ? 'Aucun crédit ce mois-ci' : undefined}
                    />
                    <CalmStatCard
                        label="Total débit"
                        value={'− ' + fmtEUR(stats.totalDebit)}
                        tone="debit"
                        meta={
                            statsMeta.debit.count > 0
                                ? [
                                      {
                                          value: String(statsMeta.debit.count),
                                          label: statsMeta.debit.count > 1 ? 'opérations' : 'opération',
                                      },
                                      {
                                          value: fmtEUR(statsMeta.debit.maxAmount),
                                          label: 'plus gros',
                                      },
                                  ]
                                : undefined
                        }
                        hint={statsMeta.debit.count === 0 ? 'Aucune dépense ce mois-ci' : undefined}
                    />
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--stats-gap)',
                        marginBottom: 'var(--stats-row2-mb)',
                    }}
                    className="calm-stats-row-2"
                >
                    <InitialBalanceCard
                        value={stats.initialBalance}
                        onSave={handleInitialBalanceSave}
                        loading={loading}
                    />
                    <CalmStatCard
                        label="Solde bancaire"
                        value={fmtEUR(stats.bankBalance, { sign: stats.bankBalance < 0 })}
                        tone={stats.bankBalance >= 0 ? 'credit' : 'debit'}
                        hint="Reflète les opérations prélevées du compte"
                    />
                </div>
            </div>

            <button
                type="button"
                onClick={() => setAddOpen(true)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--add-pad)',
                    background: t.surface,
                    border: `1px dashed ${t.borderS}`,
                    borderRadius: 'var(--add-radius)',
                    marginBottom: 'var(--add-mb)',
                    cursor: 'pointer',
                    width: '100%',
                    color: t.text,
                    fontFamily: t.fontUi,
                    transition: 'background .15s, border-color .15s',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.surface2;
                    e.currentTarget.style.borderColor = t.accent;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = t.surface;
                    e.currentTarget.style.borderColor = t.borderS;
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span
                        style={{
                            width: 'var(--add-icon)',
                            height: 'var(--add-icon)',
                            borderRadius: 10,
                            background: t.accentDim,
                            color: t.accent,
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        <IconPlus size={16} />
                    </span>
                    <span style={{ textAlign: 'left' }}>
                        <span
                            style={{
                                display: 'block',
                                fontSize: 14,
                                fontWeight: 500,
                                letterSpacing: -0.1,
                            }}
                        >
                            Ajouter une opération
                        </span>
                        <span
                            className="calm-add-subtitle"
                            style={{
                                display: 'block',
                                fontSize: 12,
                                color: t.textDim,
                                marginTop: 2,
                            }}
                        >
                            Quelques secondes suffisent
                        </span>
                    </span>
                </span>
                <span
                    style={{
                        padding: '9px 16px',
                        background: t.text,
                        color: t.bg,
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 500,
                        letterSpacing: -0.1,
                    }}
                >
                    Nouvelle opération
                </span>
            </button>

            <div
                className="calm-section-head"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                    padding: '0 4px',
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                        style={{
                            fontFamily: t.fontDisp,
                            fontSize: 20,
                            fontWeight: 500,
                            letterSpacing: -0.3,
                            color: t.text,
                        }}
                    >
                        Activité récente
                    </span>
                    <span
                        style={{
                            fontFamily: t.fontMono,
                            fontSize: 11,
                            color: t.textMute,
                            padding: '2px 8px',
                            background: t.surface,
                            border: `1px solid ${t.border}`,
                            borderRadius: 999,
                        }}
                    >
                        {txCount}
                    </span>
                </div>
            </div>

            <div>
                {grouped.length === 0 ? (
                    <div
                        style={{
                            marginTop: 16,
                            padding: '40px 24px',
                            background: t.surface,
                            border: `1px solid ${t.border}`,
                            borderRadius: 16,
                            textAlign: 'center',
                            color: t.textDim,
                            fontFamily: t.fontUi,
                        }}
                    >
                        <div
                            style={{
                                fontFamily: t.fontDisp,
                                fontSize: 20,
                                fontWeight: 500,
                                letterSpacing: -0.3,
                                color: t.text,
                                marginBottom: 6,
                            }}
                        >
                            Aucune opération
                        </div>
                        <div style={{ fontSize: 13 }}>
                            Ajoutez votre première transaction du mois avec le bouton ci-dessus.
                        </div>
                    </div>
                ) : (
                    grouped.map(([date, txs]) => (
                        <CalmDayGroup
                            key={date}
                            dateIso={date}
                            txs={txs}
                            onToggle={toggleWithdrawn}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))
                )}
            </div>

            <div
                style={{
                    textAlign: 'center',
                    padding: '32px 0 8px',
                    fontSize: 12,
                    color: t.textMute,
                    fontFamily: t.fontMono,
                    letterSpacing: 0.5,
                }}
            >
                — début du mois —
            </div>

            <CalmTransactionModal
                open={addOpen}
                mode="add"
                data={newTransaction}
                onChange={setNewTransaction}
                onSubmit={handleAddSubmit}
                onClose={() => {
                    setAddOpen(false);
                    setNewTransaction(emptyNewTx());
                }}
                loading={loading}
            />

            <CalmTransactionModal
                open={Boolean(editingTransaction)}
                mode="edit"
                data={editingTransaction || emptyNewTx()}
                onChange={setEditingTransaction}
                onSubmit={handleEditSubmit}
                onClose={() => setEditingTransaction(null)}
                loading={loading}
            />

            <CalmDeleteModal
                open={Boolean(deletingTransaction)}
                transaction={deletingTransaction}
                onConfirm={confirmDelete}
                onClose={() => setDeletingTransaction(null)}
                loading={loading}
            />

            <CalmToast feedback={feedback} onClose={closeFeedback} />

            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        display: 'grid',
                        placeItems: 'center',
                        zIndex: 40,
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            background: t.surface,
                            border: `1px solid ${t.borderS}`,
                            borderRadius: 14,
                            padding: '12px 18px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            color: t.text,
                            fontFamily: t.fontUi,
                            fontSize: 13,
                        }}
                    >
                        <Spinner className="w-5 h-5" />
                        <span>Chargement…</span>
                    </div>
                </div>
            )}

            <style>{`
                @media (max-width: 860px) {
                    .calm-stats-row-1 { grid-template-columns: 1fr !important; }
                    .calm-stats-row-2 { grid-template-columns: 1fr !important; }
                }
                /* Paysage mobile : les deux grilles se réunissent en une rangée
                   de cinq tuiles. Cette règle doit rester APRÈS celle ci-dessus,
                   qu'elle annule à priorité et spécificité égales. */
                @media (orientation: landscape) and (max-height: 520px) {
                    .calm-stats-wrap {
                        display: flex;
                        gap: var(--stats-gap);
                        align-items: stretch;
                        margin-bottom: 10px;
                    }
                    /* min-width: 0 est indispensable : en devenant des flex
                       items, les grilles héritent de min-width: auto et
                       refusent de descendre sous la largeur de leur contenu,
                       ce qui ferait déborder la rangée à droite. */
                    .calm-stats-wrap > .calm-stats-row-1 {
                        flex: 3;
                        min-width: 0;
                        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                    }
                    .calm-stats-wrap > .calm-stats-row-2 {
                        flex: 2;
                        min-width: 0;
                        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                    }
                }
            `}</style>
        </div>
    );
}
